import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { api, TimbaTournamentDetail, TimbaTournamentRef } from '../api/client';
import { getIdentity } from '../lib/identity';
import { useStore } from '../stores/store';
import { TimbaBoard } from './TimbaBoard';

// Home de la Timba: selector de torneos en cards (🌎 Mundial + privados) + tabla del seleccionado,
// con crear / unirse / compartir EN LA MISMA página. Por defecto, el Torneo Mundial.
const GLOBAL: TimbaTournamentRef = { id: 'global', name: 'Timba Mundial', global: true };

function inviteLink(token: string): string {
  return window.location.origin + (process.env.PUBLIC_URL || '') + '/timba/t/' + token;
}

// Copia texto: API moderna en contexto seguro, si no fallback legado (sirve sobre HTTP en la LAN).
async function copyText(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function TimbaTournaments() {
  const { token: joinToken } = useParams();
  const nick = useStore((s) => s.nick);
  const setNick = useStore((s) => s.setNick);
  const pub = (() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  })();

  const [list, setList] = useState<TimbaTournamentRef[]>([GLOBAL]);
  const [selected, setSelected] = useState('global');
  const [detail, setDetail] = useState<TimbaTournamentDetail | null>(null);
  const [panel, setPanel] = useState<'' | 'create' | 'join'>('');
  const [name, setName] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [toast, setToast] = useState('');

  const flash = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(''), 3500);
  };

  const reload = useCallback(async () => {
    if (!pub) return;
    const d = await api.timbaTournaments(pub).catch(() => null);
    if (d && d.tournaments.length) setList(d.tournaments);
  }, [pub]);

  // Carga inicial + auto-unión si se entró por un link de invitación (/timba/t/:token).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (joinToken && pub) {
        const r = await api.timbaJoinTournament(pub, nick, joinToken).catch(() => null);
        if (r && alive) {
          if (r.nick && r.nick !== nick) setNick(r.nick);
          await reload();
          setSelected(r.id);
          flash(`Te uniste a "${r.name}"`);
          return;
        }
      }
      await reload();
    })();
    return () => {
      alive = false;
    };
  }, [joinToken, pub, nick, reload, setNick]);

  // Tabla del torneo seleccionado.
  useEffect(() => {
    let alive = true;
    setDetail(null);
    api
      .timbaTournament(selected, pub)
      .then((d) => alive && setDetail(d))
      .catch(() => alive && setDetail(null));
    return () => {
      alive = false;
    };
  }, [selected, pub]);

  const create = async () => {
    const clean = name.trim();
    if (clean.length < 2) return;
    const r = await api.timbaCreateTournament(pub, nick, clean).catch(() => null);
    if (r) {
      if (r.nick && r.nick !== nick) setNick(r.nick);
      setName('');
      setPanel('');
      await reload();
      setSelected(r.id);
      flash(`Torneo "${r.name}" creado`);
    }
  };

  const join = async () => {
    const tok = joinInput.trim().replace(/.*\/timba\/t\//, '');
    if (!tok) return;
    const r = await api.timbaJoinTournament(pub, nick, tok).catch(() => null);
    if (r) {
      if (r.nick && r.nick !== nick) setNick(r.nick);
      setJoinInput('');
      setPanel('');
      await reload();
      setSelected(r.id);
      flash(`Te uniste a "${r.name}"`);
    } else {
      flash('No se encontró ese torneo');
    }
  };

  const selRef = list.find((t) => t.id === selected);
  const share = async () => {
    if (!selRef?.token) return;
    const url = inviteLink(selRef.token);
    const text = `Sumate a mi torneo "${selRef.name}" en VamoVamo!\n${url}\n\n📲 ¿Ya tenés la app instalada? Copiá este link y pegalo en «＋ Torneo → Unirme con link» (así no se duplica tu usuario).`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Timba — ${selRef.name}`, text });
        return;
      } catch {
        return;
      }
    }
    const ok = await copyText(text);
    flash(ok ? '📋 ¡Invitación copiada! Pegásela a tus amigos por WhatsApp. 🎉' : `Copiá y compartí: ${url}`);
  };

  return (
    <Wrap>
      <Cards>
        {list.map((t) => (
          <button key={t.id} type="button" className={t.id === selected ? 'on' : ''} onClick={() => { setSelected(t.id); setPanel(''); }}>
            <span className="ico">{t.global ? '🌎' : '🏆'}</span>
            <span className="nm">{t.name}</span>
          </button>
        ))}
        <button type="button" className={`add ${panel ? 'on' : ''}`} onClick={() => setPanel(panel ? '' : 'create')}>
          <span className="ico">＋</span>
          <span className="nm">Torneo</span>
        </button>
      </Cards>

      {panel && (
        <Panel>
          <div className="tabs">
            <button type="button" className={panel === 'create' ? 'on' : ''} onClick={() => setPanel('create')}>Crear</button>
            <button type="button" className={panel === 'join' ? 'on' : ''} onClick={() => setPanel('join')}>Unirme con link</button>
          </div>
          {panel === 'create' ? (
            <Form>
              <input value={name} placeholder="Nombre del torneo (ej. Los Pibes)" maxLength={60} autoComplete="off" autoCorrect="off" spellCheck={false} onChange={(e) => setName(e.target.value)} />
              <button type="button" onClick={create} disabled={name.trim().length < 2}>Crear</button>
            </Form>
          ) : (
            <>
              <Form>
                <input value={joinInput} placeholder="Pegá el link o el código de invitación" autoComplete="off" autoCorrect="off" spellCheck={false} onChange={(e) => setJoinInput(e.target.value)} />
                <button type="button" onClick={join} disabled={!joinInput.trim()}>Unirme</button>
              </Form>
              <p className="hint">📲 Si tenés la app instalada en tu pantalla de inicio, pegá acá el link en vez de abrirlo: así te unís con tu identidad de siempre (no se duplica tu usuario).</p>
            </>
          )}
        </Panel>
      )}

      {detail && (
        <Board>
          <div className="head">
            <span className="nm">{selected === 'global' ? '🌎' : '🏆'} {detail.name}</span>
            <span className="ct">
              {detail.members} {detail.members === 1 ? 'participante' : 'participantes'}
            </span>
          </div>
          <TimbaBoard standings={detail.standings} pub={pub} top={10} />
          {selRef && !selRef.global && selRef.token && (
            <button type="button" className="share" onClick={share}>Compartir invitación</button>
          )}
        </Board>
      )}

      {toast && <Toast>{toast}</Toast>}
    </Wrap>
  );
}

const Wrap = styled.div`
  margin-bottom: 18px;
`;

const Cards = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 12px;

  button {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-width: 96px;
    max-width: 150px;
    padding: 10px 12px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
    text-align: center;
  }
  .ico { font-size: 1.25rem; line-height: 1; }
  .nm { max-width: 124px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.82rem; }
  button.on {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border-color: ${({ theme }) => theme.colors.accent};
  }
  .add {
    min-width: 70px;
    color: ${({ theme }) => theme.colors.textMuted};
    border-style: dashed;
  }
`;

const Panel = styled.div`
  margin-bottom: 12px;
  .tabs { display: flex; gap: 8px; margin-bottom: 8px; }
  .tabs button {
    flex: 1;
    padding: 8px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: none;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
    font-size: 0.85rem;
  }
  .tabs button.on { background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText}; border-color: ${({ theme }) => theme.colors.accent}; }
  .hint { font-size: 0.76rem; color: ${({ theme }) => theme.colors.textMuted}; margin: 8px 2px 0; line-height: 1.4; }
`;

const Form = styled.div`
  display: flex;
  gap: 8px;
  input {
    flex: 1;
    min-width: 0;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 0.9rem;
  }
  button {
    flex: 0 0 auto;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
    border-radius: 10px;
    padding: 0 16px;
    font-weight: 700;
  }
  button:disabled { opacity: 0.5; }
`;

const Board = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 12px 14px;
    background: ${({ theme }) => theme.colors.surfaceAlt};
  }
  .head .nm { font-weight: 800; font-size: 1.05rem; }
  .head .ct { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .share {
    width: 100%;
    background: none;
    color: ${({ theme }) => theme.colors.accent};
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    padding: 12px;
    font-weight: 700;
    font-size: 0.9rem;
  }
`;

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 88px;
  transform: translateX(-50%);
  z-index: 50;
  width: calc(100% - 32px);
  max-width: 380px;
  text-align: center;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 16px;
  padding: 12px 18px;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;
